"use client";

import { useMemo, useSyncExternalStore } from "react";
import styles from "./Checklist.module.css";

interface ChecklistItem {
  id: string;
  text: string;
  checked: boolean;
  children?: ChecklistItem[];
}

interface ChecklistProps {
  id: string;
  items: Array<{ text: string; id?: string }>;
}

const CHANGE_EVENT = "fivem-docs-checklist-changed";

function subscribe(notify: () => void) {
  window.addEventListener("storage", notify);
  window.addEventListener(CHANGE_EVENT, notify);
  return () => {
    window.removeEventListener("storage", notify);
    window.removeEventListener(CHANGE_EVENT, notify);
  };
}

export default function Checklist({ id, items }: ChecklistProps) {
  const storageKey = `fivem-docs-checklist-${id}`;

  // Server snapshot is null, so hydration renders the defaults and the saved state follows (no #418 mismatch).
  const stored = useSyncExternalStore(
    subscribe,
    () => localStorage.getItem(storageKey),
    () => null,
  );
  const checklistItems = useMemo<ChecklistItem[]>(() => {
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch {
        // Fallback to default
      }
    }
    return items.map((item, index) => ({
      id: item.id || `item-${index}`,
      text: item.text,
      checked: false,
    }));
  }, [stored, items]);

  const toggleItem = (itemId: string) => {
    const next = checklistItems.map((item) => {
      if (item.id === itemId) {
        return { ...item, checked: !item.checked };
      }
      if (item.children) {
        return {
          ...item,
          children: item.children.map((child) =>
            child.id === itemId ? { ...child, checked: !child.checked } : child,
          ),
        };
      }
      return item;
    });
    localStorage.setItem(storageKey, JSON.stringify(next));
    window.dispatchEvent(new Event(CHANGE_EVENT));
  };

  return (
    <div className={styles.checklist}>
      <ul className={styles.checklistList}>
        {checklistItems.map((item) => (
          <li className={styles.checklistItem} key={item.id}>
            <label className={styles.checklistLabel}>
              <input
                checked={item.checked}
                className={styles.checklistCheckbox}
                onChange={() => toggleItem(item.id)}
                type="checkbox"
              />
              <span
                className={
                  item.checked
                    ? styles.checklistTextChecked
                    : styles.checklistText
                }
              >
                {item.text}
              </span>
            </label>
            {item.children && item.children.length > 0 && (
              <ul className={styles.checklistSublist}>
                {item.children.map((child) => (
                  <li className={styles.checklistItem} key={child.id}>
                    <label className={styles.checklistLabel}>
                      <input
                        checked={child.checked}
                        className={styles.checklistCheckbox}
                        onChange={() => toggleItem(child.id)}
                        type="checkbox"
                      />
                      <span
                        className={
                          child.checked
                            ? styles.checklistTextChecked
                            : styles.checklistText
                        }
                      >
                        {child.text}
                      </span>
                    </label>
                  </li>
                ))}
              </ul>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
